export type ProductCategory = 'urban-security' | 'access-control' | 'special-projects'

export interface ProductFeature {
  titleKey: string
  descKey: string
}

export interface ProductDownload {
  labelKey: string
  url: string
}

export interface Product {
  id: string
  name: string
  category: ProductCategory
  images: string[]
  descriptionKey: string
  subtitleKey: string
  fullDescriptionKey: string
  features: ProductFeature[]
  applicationsKey: string
  downloads: ProductDownload[]
  videoUrl?: string
  slug: {
    it: string
    en: string
  }
}

export interface ProductCategoryInfo {
  id: ProductCategory
  nameKey: string
  icon: string
  iconWhite: string
  heroImage: string
  descriptionKey: string
  slug: {
    it: string
    en: string
  }
}

export const productCategories: ProductCategoryInfo[] = [
  {
    id: 'urban-security',
    nameKey: 'products.categories.urbanSecurity',
    icon: '/media/icons/sicurezza-urbana.svg',
    iconWhite: '/media/icons/sicurezza-urbana-white.svg',
    heroImage: '/media/sectors/telecamere-sicurezza-urbana.jpg',
    descriptionKey: 'products.categories.urbanSecurityDesc',
    slug: {
      it: 'sicurezza-urbana',
      en: 'urban-security',
    },
  },
  {
    id: 'access-control',
    nameKey: 'products.categories.accessControl',
    icon: '/media/icons/controllo-accessi.svg',
    iconWhite: '/media/icons/controllo-accessi-white.svg',
    heroImage: '/media/sectors/telecamere-controllo-accessi.jpg',
    descriptionKey: 'products.categories.accessControlDesc',
    slug: {
      it: 'controllo-accessi',
      en: 'access-control',
    },
  },
  {
    id: 'special-projects',
    nameKey: 'products.categories.specialProjects',
    icon: '/media/icons/progetti-speciali.svg',
    iconWhite: '/media/icons/progetti-speciali-white.svg',
    heroImage: '/media/sectors/progetti-speciali.jpg',
    descriptionKey: 'products.categories.specialProjectsDesc',
    slug: {
      it: 'progetti-speciali',
      en: 'special-projects',
    },
  },
]

export const products: Product[] = [
  // Sicurezza Urbana
  {
    id: 'targa-306',
    name: 'Targa 306',
    category: 'urban-security',
    images: ['/media/products/targa-306.png', '/media/products/targa-206-variant-2.png', '/media/products/targa-206-variant-3.png', '/media/products/targa-206-variant-4.png'],
    descriptionKey: 'products.items.targa306',
    subtitleKey: 'products.detail.targa306.subtitle',
    fullDescriptionKey: 'products.detail.targa306.fullDescription',
    features: [
      { titleKey: 'products.detail.targa306.features.algorithms.title', descKey: 'products.detail.targa306.features.algorithms.desc' },
      { titleKey: 'products.detail.targa306.features.ocrSensor.title', descKey: 'products.detail.targa306.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa306.features.contextSensor.title', descKey: 'products.detail.targa306.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa306.features.privacy.title', descKey: 'products.detail.targa306.features.privacy.desc' },
      { titleKey: 'products.detail.targa306.features.fogFighter.title', descKey: 'products.detail.targa306.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa306.features.certifications.title', descKey: 'products.detail.targa306.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa306.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA306_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_J_T306_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Urbana_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-306', en: 'targa-306' },
  },
  {
    id: 'targa-706',
    name: 'Targa 706',
    category: 'urban-security',
    images: ['/media/products/targa-706.png', '/media/products/targa-706-variant-2.png', '/media/products/targa-706-variant-3.png', '/media/products/targa-706-variant-4.png'],
    descriptionKey: 'products.items.targa706',
    subtitleKey: 'products.detail.targa706.subtitle',
    fullDescriptionKey: 'products.detail.targa706.fullDescription',
    features: [
      { titleKey: 'products.detail.targa706.features.modularity.title', descKey: 'products.detail.targa706.features.modularity.desc' },
      { titleKey: 'products.detail.targa706.features.algorithms.title', descKey: 'products.detail.targa706.features.algorithms.desc' },
      { titleKey: 'products.detail.targa706.features.ocrSensor.title', descKey: 'products.detail.targa706.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa706.features.contextSensor.title', descKey: 'products.detail.targa706.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa706.features.accuracy.title', descKey: 'products.detail.targa706.features.accuracy.desc' },
      { titleKey: 'products.detail.targa706.features.fogFighter.title', descKey: 'products.detail.targa706.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa706.features.memory.title', descKey: 'products.detail.targa706.features.memory.desc' },
      { titleKey: 'products.detail.targa706.features.certifications.title', descKey: 'products.detail.targa706.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa706.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA706_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_J_T706_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Urbana_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.videoAnalysisReport', url: 'https://cdn.selea.com/DATASHEET_2025/SELEA_AutoProfile_Report.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-706', en: 'targa-706' },
  },
  {
    id: 'targa-760',
    name: 'Targa 760',
    category: 'urban-security',
    images: ['/media/products/targa-760.png', '/media/products/targa-760-variant-2.png', '/media/products/targa-760-variant-3.png', '/media/products/targa-760-variant-4.png'],
    descriptionKey: 'products.items.targa760',
    subtitleKey: 'products.detail.targa760.subtitle',
    fullDescriptionKey: 'products.detail.targa760.fullDescription',
    features: [
      { titleKey: 'products.detail.targa760.features.modularity.title', descKey: 'products.detail.targa760.features.modularity.desc' },
      { titleKey: 'products.detail.targa760.features.algorithms.title', descKey: 'products.detail.targa760.features.algorithms.desc' },
      { titleKey: 'products.detail.targa760.features.ocrSensor.title', descKey: 'products.detail.targa760.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa760.features.contextSensor.title', descKey: 'products.detail.targa760.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa760.features.accuracy.title', descKey: 'products.detail.targa760.features.accuracy.desc' },
      { titleKey: 'products.detail.targa760.features.fogFighter.title', descKey: 'products.detail.targa760.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa760.features.memory.title', descKey: 'products.detail.targa760.features.memory.desc' },
      { titleKey: 'products.detail.targa760.features.certifications.title', descKey: 'products.detail.targa760.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa760.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA760_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_J_T760_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Urbana_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.videoAnalysisReport', url: 'https://cdn.selea.com/DATASHEET_2025/SELEA_AutoProfile_Report.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-760', en: 'targa-760' },
  },
  {
    id: 'targa-860',
    name: 'Targa 860',
    category: 'urban-security',
    images: ['/media/products/targa-860.png', '/media/products/targa-860-variant-2.png', '/media/products/targa-860-variant-3.png', '/media/products/targa-860-variant-4.png'],
    descriptionKey: 'products.items.targa860',
    subtitleKey: 'products.detail.targa860.subtitle',
    fullDescriptionKey: 'products.detail.targa860.fullDescription',
    features: [
      { titleKey: 'products.detail.targa860.features.modularity.title', descKey: 'products.detail.targa860.features.modularity.desc' },
      { titleKey: 'products.detail.targa860.features.algorithms.title', descKey: 'products.detail.targa860.features.algorithms.desc' },
      { titleKey: 'products.detail.targa860.features.privacy.title', descKey: 'products.detail.targa860.features.privacy.desc' },
      { titleKey: 'products.detail.targa860.features.ocrSensor.title', descKey: 'products.detail.targa860.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa860.features.contextSensor.title', descKey: 'products.detail.targa860.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa860.features.accuracy.title', descKey: 'products.detail.targa860.features.accuracy.desc' },
      { titleKey: 'products.detail.targa860.features.fogFighter.title', descKey: 'products.detail.targa860.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa860.features.memory.title', descKey: 'products.detail.targa860.features.memory.desc' },
      { titleKey: 'products.detail.targa860.features.certifications.title', descKey: 'products.detail.targa860.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa860.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA860_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_J_T860_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Urbana_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.videoAnalysisReport', url: 'https://cdn.selea.com/DATASHEET_2025/SELEA_AutoProfile_Report.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-860', en: 'targa-860' },
  },
  {
    id: 'targa-960',
    name: 'Targa 960',
    category: 'urban-security',
    images: ['/media/products/targa-960.png', '/media/products/targa-960-variant-2.png', '/media/products/targa-960-variant-3.png', '/media/products/targa-960-variant-4.png'],
    descriptionKey: 'products.items.targa960',
    subtitleKey: 'products.detail.targa960.subtitle',
    fullDescriptionKey: 'products.detail.targa960.fullDescription',
    features: [
      { titleKey: 'products.detail.targa960.features.multiVideoAnalysis.title', descKey: 'products.detail.targa960.features.multiVideoAnalysis.desc' },
      { titleKey: 'products.detail.targa960.features.ocrSensor.title', descKey: 'products.detail.targa960.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa960.features.contextSensor.title', descKey: 'products.detail.targa960.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa960.features.privacy.title', descKey: 'products.detail.targa960.features.privacy.desc' },
      { titleKey: 'products.detail.targa960.features.accuracy.title', descKey: 'products.detail.targa960.features.accuracy.desc' },
      { titleKey: 'products.detail.targa960.features.fogFighter.title', descKey: 'products.detail.targa960.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa960.features.memory.title', descKey: 'products.detail.targa960.features.memory.desc' },
      { titleKey: 'products.detail.targa960.features.certifications.title', descKey: 'products.detail.targa960.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa960.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA960_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_J_T960_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Urbana_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.videoAnalysisReport', url: 'https://cdn.selea.com/DATASHEET_2025/SELEA_AutoProfile_Report.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-960', en: 'targa-960' },
  },
  {
    id: 'targa-scanner',
    name: 'Targa Scanner',
    category: 'urban-security',
    images: ['/media/products/targa-scanner.jpg', '/media/products/targa-scanner-600x602.jpg', '/media/products/targa-scanner-laterale-600x600.jpg', '/media/products/targa-scanner-mobile-600x600.jpg'],
    descriptionKey: 'products.items.targaScanner',
    subtitleKey: 'products.detail.targaScanner.subtitle',
    fullDescriptionKey: 'products.detail.targaScanner.fullDescription',
    features: [
      { titleKey: 'products.detail.targaScanner.features.ts8.title', descKey: 'products.detail.targaScanner.features.ts8.desc' },
      { titleKey: 'products.detail.targaScanner.features.ts25.title', descKey: 'products.detail.targaScanner.features.ts25.desc' },
      { titleKey: 'products.detail.targaScanner.features.accuracy.title', descKey: 'products.detail.targaScanner.features.accuracy.desc' },
      { titleKey: 'products.detail.targaScanner.features.fogFighter.title', descKey: 'products.detail.targaScanner.features.fogFighter.desc' },
      { titleKey: 'products.detail.targaScanner.features.certifications.title', descKey: 'products.detail.targaScanner.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targaScanner.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TargaScanner_IT_032025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-scanner', en: 'targa-scanner' },
  },
  {
    id: 'cps-6-5',
    name: 'CPS 6.5 Smart City',
    category: 'urban-security',
    images: ['/media/products/cps-polizia-locale.jpg', '/media/products/cps-screenshot-1.jpg', '/media/products/cps-screenshot-2.jpg'],
    descriptionKey: 'products.items.cps65',
    subtitleKey: 'products.detail.cps65.subtitle',
    fullDescriptionKey: 'products.detail.cps65.fullDescription',
    features: [
      { titleKey: 'products.detail.cps65.features.zeroFees.title', descKey: 'products.detail.cps65.features.zeroFees.desc' },
      { titleKey: 'products.detail.cps65.features.neuralNetworks.title', descKey: 'products.detail.cps65.features.neuralNetworks.desc' },
      { titleKey: 'products.detail.cps65.features.investigations.title', descKey: 'products.detail.cps65.features.investigations.desc' },
      { titleKey: 'products.detail.cps65.features.integration.title', descKey: 'products.detail.cps65.features.integration.desc' },
      { titleKey: 'products.detail.cps65.features.multiFunctions.title', descKey: 'products.detail.cps65.features.multiFunctions.desc' },
      { titleKey: 'products.detail.cps65.features.privacyCert.title', descKey: 'products.detail.cps65.features.privacyCert.desc' },
    ],
    applicationsKey: 'products.detail.cps65.applications',
    downloads: [
      { labelKey: 'products.detail.common.brochure', url: 'https://cdn.selea.com/DATASHEET_2024/CPS65_Brochure_IT_032024.pdf' },
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/CPS65_Datesheet_IT_032025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
    ],
    slug: { it: 'cps-6-5', en: 'cps-6-5' },
  },
  {
    id: 'cps-manager',
    name: 'CPS Manager',
    category: 'urban-security',
    images: ['/media/products/cps-manager.jpg', '/media/products/cps-manager-600x600.jpg', '/media/products/cps-manager-complici-600x338.jpg', '/media/products/cps-manager-alerts-theft-600x338.jpg'],
    descriptionKey: 'products.items.cpsManager',
    subtitleKey: 'products.detail.cpsManager.subtitle',
    fullDescriptionKey: 'products.detail.cpsManager.fullDescription',
    features: [
      { titleKey: 'products.detail.cpsManager.features.advantages.title', descKey: 'products.detail.cpsManager.features.advantages.desc' },
      { titleKey: 'products.detail.cpsManager.features.privacy.title', descKey: 'products.detail.cpsManager.features.privacy.desc' },
    ],
    applicationsKey: 'products.detail.cpsManager.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/CPS_MANAGER_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
    ],
    slug: { it: 'cps-manager', en: 'cps-manager' },
  },
  // Controllo Accessi
  {
    id: 'izero-2',
    name: 'iZero 2',
    category: 'access-control',
    images: ['/media/products/izero2.png', '/media/products/izero2-600x600.png', '/media/products/izero2-variant-2.png', '/media/products/izero2-variant-3.png'],
    descriptionKey: 'products.items.izero2',
    subtitleKey: 'products.detail.izero2.subtitle',
    fullDescriptionKey: 'products.detail.izero2.fullDescription',
    features: [
      { titleKey: 'products.detail.izero2.features.accuracy.title', descKey: 'products.detail.izero2.features.accuracy.desc' },
      { titleKey: 'products.detail.izero2.features.readingDistance.title', descKey: 'products.detail.izero2.features.readingDistance.desc' },
      { titleKey: 'products.detail.izero2.features.integration.title', descKey: 'products.detail.izero2.features.integration.desc' },
      { titleKey: 'products.detail.izero2.features.memory.title', descKey: 'products.detail.izero2.features.memory.desc' },
      { titleKey: 'products.detail.izero2.features.io.title', descKey: 'products.detail.izero2.features.io.desc' },
      { titleKey: 'products.detail.izero2.features.fogFighter.title', descKey: 'products.detail.izero2.features.fogFighter.desc' },
      { titleKey: 'products.detail.izero2.features.certifications.title', descKey: 'products.detail.izero2.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.izero2.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/iZero2_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_iZero2_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Controllo_Accessi_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'izero-2', en: 'izero-2' },
  },
  {
    id: 'izero-box',
    name: 'iZero BOX',
    category: 'access-control',
    images: ['/media/products/izero-box.png', '/media/products/izero-box-variant-2.png', '/media/products/izero-box-dimension.png'],
    descriptionKey: 'products.items.izeroBox',
    subtitleKey: 'products.detail.izeroBox.subtitle',
    fullDescriptionKey: 'products.detail.izeroBox.fullDescription',
    features: [
      { titleKey: 'products.detail.izeroBox.features.accuracy.title', descKey: 'products.detail.izeroBox.features.accuracy.desc' },
      { titleKey: 'products.detail.izeroBox.features.readingDistance.title', descKey: 'products.detail.izeroBox.features.readingDistance.desc' },
      { titleKey: 'products.detail.izeroBox.features.memory.title', descKey: 'products.detail.izeroBox.features.memory.desc' },
      { titleKey: 'products.detail.izeroBox.features.io.title', descKey: 'products.detail.izeroBox.features.io.desc' },
      { titleKey: 'products.detail.izeroBox.features.fogFighter.title', descKey: 'products.detail.izeroBox.features.fogFighter.desc' },
      { titleKey: 'products.detail.izeroBox.features.certifications.title', descKey: 'products.detail.izeroBox.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.izeroBox.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/iZEROBOX_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_iZeroBOX_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Controllo_Accessi_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'izero-box', en: 'izero-box' },
  },
  {
    id: 'targa-512',
    name: 'Targa 512',
    category: 'access-control',
    images: ['/media/products/targa-512.png', '/media/products/targa-512-variant-2.png', '/media/products/targa-512-variant-3.png', '/media/products/targa-512-variant-4.png'],
    descriptionKey: 'products.items.targa512',
    subtitleKey: 'products.detail.targa512.subtitle',
    fullDescriptionKey: 'products.detail.targa512.fullDescription',
    features: [
      { titleKey: 'products.detail.targa512.features.accuracy.title', descKey: 'products.detail.targa512.features.accuracy.desc' },
      { titleKey: 'products.detail.targa512.features.integration.title', descKey: 'products.detail.targa512.features.integration.desc' },
      { titleKey: 'products.detail.targa512.features.memory.title', descKey: 'products.detail.targa512.features.memory.desc' },
      { titleKey: 'products.detail.targa512.features.io.title', descKey: 'products.detail.targa512.features.io.desc' },
      { titleKey: 'products.detail.targa512.features.fogFighter.title', descKey: 'products.detail.targa512.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa512.features.certifications.title', descKey: 'products.detail.targa512.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa512.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA512_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_X_T512_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Controllo_Accessi_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-512', en: 'targa-512' },
  },
  {
    id: 'targa-504',
    name: 'Targa 504',
    category: 'access-control',
    images: ['/media/products/targa-504.png', '/media/products/targa-504-variant-2.png', '/media/products/targa-504-variant-3.jpg'],
    descriptionKey: 'products.items.targa504',
    subtitleKey: 'products.detail.targa504.subtitle',
    fullDescriptionKey: 'products.detail.targa504.fullDescription',
    features: [
      { titleKey: 'products.detail.targa504.features.optics.title', descKey: 'products.detail.targa504.features.optics.desc' },
      { titleKey: 'products.detail.targa504.features.accuracy.title', descKey: 'products.detail.targa504.features.accuracy.desc' },
      { titleKey: 'products.detail.targa504.features.memory.title', descKey: 'products.detail.targa504.features.memory.desc' },
      { titleKey: 'products.detail.targa504.features.io.title', descKey: 'products.detail.targa504.features.io.desc' },
      { titleKey: 'products.detail.targa504.features.fogFighter.title', descKey: 'products.detail.targa504.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa504.features.certifications.title', descKey: 'products.detail.targa504.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa504.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA504_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_X_T504_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Controllo_Accessi_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-504', en: 'targa-504' },
  },
  {
    id: 'targa-560',
    name: 'Targa 560',
    category: 'access-control',
    images: ['/media/products/targa-560.png', '/media/products/targa-560-variant-2.png', '/media/products/targa-560-variant-3.png', '/media/products/targa-560-variant-4.png'],
    descriptionKey: 'products.items.targa560',
    subtitleKey: 'products.detail.targa560.subtitle',
    fullDescriptionKey: 'products.detail.targa560.fullDescription',
    features: [
      { titleKey: 'products.detail.targa560.features.illuminator.title', descKey: 'products.detail.targa560.features.illuminator.desc' },
      { titleKey: 'products.detail.targa560.features.ocrSensor.title', descKey: 'products.detail.targa560.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa560.features.contextSensor.title', descKey: 'products.detail.targa560.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa560.features.accuracy.title', descKey: 'products.detail.targa560.features.accuracy.desc' },
      { titleKey: 'products.detail.targa560.features.fogFighter.title', descKey: 'products.detail.targa560.features.fogFighter.desc' },
      { titleKey: 'products.detail.targa560.features.memory.title', descKey: 'products.detail.targa560.features.memory.desc' },
      { titleKey: 'products.detail.targa560.features.certifications.title', descKey: 'products.detail.targa560.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa560.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA560_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/Man_utente_TARGA_J_T560_IT.pdf' },
      { labelKey: 'products.detail.common.comparativeTable', url: 'https://cdn.selea.com/DATASHEET_2025/Tabella_Comparativa_Controllo_Accessi_IT_2025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-560', en: 'targa-560' },
  },
  {
    id: 'qentry-smart-parking',
    name: 'Qentry Smart Parking',
    category: 'access-control',
    images: ['/media/products/qentry-smart-parking.png', '/media/products/qentry-smart-parking-600x600.png'],
    descriptionKey: 'products.items.qentry',
    subtitleKey: 'products.detail.qentry.subtitle',
    fullDescriptionKey: 'products.detail.qentry.fullDescription',
    features: [
      { titleKey: 'products.detail.qentry.features.permits.title', descKey: 'products.detail.qentry.features.permits.desc' },
      { titleKey: 'products.detail.qentry.features.specialVehicles.title', descKey: 'products.detail.qentry.features.specialVehicles.desc' },
      { titleKey: 'products.detail.qentry.features.residential.title', descKey: 'products.detail.qentry.features.residential.desc' },
      { titleKey: 'products.detail.qentry.features.tariffs.title', descKey: 'products.detail.qentry.features.tariffs.desc' },
      { titleKey: 'products.detail.qentry.features.antiPassback.title', descKey: 'products.detail.qentry.features.antiPassback.desc' },
      { titleKey: 'products.detail.qentry.features.searchEngine.title', descKey: 'products.detail.qentry.features.searchEngine.desc' },
    ],
    applicationsKey: 'products.detail.qentry.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/Qentry_IT_032025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
    ],
    slug: { it: 'qentry-smart-parking', en: 'qentry-smart-parking' },
  },
  // Progetti Speciali
  {
    id: 'targa-706-ilb',
    name: 'Targa 706 ILB',
    category: 'special-projects',
    images: ['/media/products/targa-706-ilb.png', '/media/products/targa-706-ilb-variant-2.png', '/media/products/targa-706-ilb-variant-3.png', '/media/products/targa-706-ilb-variant-4.png'],
    descriptionKey: 'products.items.targa706ilb',
    subtitleKey: 'products.detail.targa706ilb.subtitle',
    fullDescriptionKey: 'products.detail.targa706ilb.fullDescription',
    features: [
      { titleKey: 'products.detail.targa706ilb.features.ocrSensor.title', descKey: 'products.detail.targa706ilb.features.ocrSensor.desc' },
      { titleKey: 'products.detail.targa706ilb.features.contextSensor.title', descKey: 'products.detail.targa706ilb.features.contextSensor.desc' },
      { titleKey: 'products.detail.targa706ilb.features.accuracy.title', descKey: 'products.detail.targa706ilb.features.accuracy.desc' },
      { titleKey: 'products.detail.targa706ilb.features.memory.title', descKey: 'products.detail.targa706ilb.features.memory.desc' },
      { titleKey: 'products.detail.targa706ilb.features.certifications.title', descKey: 'products.detail.targa706ilb.features.certifications.desc' },
    ],
    applicationsKey: 'products.detail.targa706ilb.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/TARGA706ILB_IT_032025.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'targa-706-ilb', en: 'targa-706-ilb' },
  },
  {
    id: 'targa-inox',
    name: 'Targa INOX',
    category: 'special-projects',
    images: ['/media/products/targa-inox.png'],
    descriptionKey: 'products.items.targaInox',
    subtitleKey: 'products.detail.targaInox.subtitle',
    fullDescriptionKey: 'products.detail.targaInox.fullDescription',
    features: [
      { titleKey: 'products.detail.targaInox.features.cleanGlass.title', descKey: 'products.detail.targaInox.features.cleanGlass.desc' },
      { titleKey: 'products.detail.targaInox.features.poleCollar.title', descKey: 'products.detail.targaInox.features.poleCollar.desc' },
    ],
    applicationsKey: 'products.detail.targaInox.applications',
    downloads: [
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
    ],
    slug: { it: 'targa-inox', en: 'targa-inox' },
  },
  {
    id: 'ocr-box',
    name: 'OCR BOX',
    category: 'special-projects',
    images: ['/media/products/ocrbox.jpg', '/media/products/ocrbox-variant-2.png', '/media/products/ocrbox-variant-4.jpg'],
    descriptionKey: 'products.items.ocrBox',
    subtitleKey: 'products.detail.ocrBox.subtitle',
    fullDescriptionKey: 'products.detail.ocrBox.fullDescription',
    features: [
      { titleKey: 'products.detail.ocrBox.features.extraction.title', descKey: 'products.detail.ocrBox.features.extraction.desc' },
      { titleKey: 'products.detail.ocrBox.features.makeModel.title', descKey: 'products.detail.ocrBox.features.makeModel.desc' },
      { titleKey: 'products.detail.ocrBox.features.timeEfficiency.title', descKey: 'products.detail.ocrBox.features.timeEfficiency.desc' },
    ],
    applicationsKey: 'products.detail.ocrBox.applications',
    downloads: [
      { labelKey: 'products.detail.common.datasheet', url: 'https://cdn.selea.com/DATASHEET_2025/OCRBOX_IT_032025.pdf' },
      { labelKey: 'products.detail.common.manual', url: 'https://cdn.selea.com/MANUALI/IT/OCRBOX_IT_Manuale_032024.pdf' },
      { labelKey: 'products.detail.common.catalog', url: 'https://cdn.selea.com/DATASHEET_2025/Catalogo_Prodotti_IT_032025.pdf' },
      { labelKey: 'products.detail.common.isdp10003', url: 'https://cdn.selea.com/specialdoc_2024/ISDP10003_IT.pdf' },
      { labelKey: 'products.detail.common.ndaa', url: 'https://cdn.selea.com/specialdoc_2024/IT_Conformita_NDAA.pdf' },
    ],
    slug: { it: 'ocr-box', en: 'ocr-box' },
  },
]

export function getProductsByCategory(category: ProductCategory): Product[] {
  return products.filter((p) => p.category === category)
}

export function getProductBySlug(slug: string): Product | undefined {
  return products.find((p) => p.slug.it === slug || p.slug.en === slug)
}

export function getCategoryBySlug(slug: string): ProductCategoryInfo | undefined {
  return productCategories.find((c) => c.slug.it === slug || c.slug.en === slug)
}
